import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Swal from '../librarianSwal';
import { useActiveLibraryId } from '../../Constant/useActiveLibrary';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';
import '../lib-page-header.css';
import '../lib-typography.css';
import './ReceiptPrint.css';
import Pagination from '../components/Pagination';

import {
  getPaymentsByLibrary,
  getErrorMessage
} from '../../api/api';
import { readPrefetched } from '../../api/prefetchCache';

const PAYMENTS_RESOURCE = 'payments';
const PAGE_SIZE = 10;

const ReceiptPrint = () => {
  const libraryId = useActiveLibraryId();
  const location = useLocation();

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const autoPrintedForRef = useRef(null);

  const fetchPayments = async () => {
    try {
      if (!libraryId) return;

      setLoading(true);
      const data = await getPaymentsByLibrary(libraryId);
      setPayments(Array.isArray(data) ? data : []);
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const warm = readPrefetched(PAYMENTS_RESOURCE, libraryId);
    if (warm) setPayments(Array.isArray(warm) ? warm : []);

    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  const handlePrint = (payment) => {
    setSelectedPayment(payment);

    setTimeout(() => {
      window.print();
    }, 300);
  };

  // Hand-off from the Members page "Print Receipt" action: once the library's
  // payments have loaded, jump straight to that member's receipt and open
  // the print dialog — without this, landing here would just show the plain
  // list and the librarian would have to hunt down the right row manually.
  useEffect(() => {
    const paymentId = location.state?.paymentId;
    if (!paymentId || !payments.length) return;
    if (autoPrintedForRef.current === paymentId) return;

    const target = payments.find((payment) => String(payment.id) === String(paymentId));
    if (!target) return;

    autoPrintedForRef.current = paymentId;
    handlePrint(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments, location.state]);

  const getPaymentName = (payment) => payment.member_name || payment.parent_name || '-';

  const filteredPayments = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return payments;

    return payments.filter((payment) => {
      return (
        String(getPaymentName(payment)).toLowerCase().includes(keyword) ||
        String(payment.mobile || '').toLowerCase().includes(keyword) ||
        String(payment.payment_type || '').toLowerCase().includes(keyword) ||
        String(payment.transaction_ref || '').toLowerCase().includes(keyword)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments, search]);

  const totalPages = Math.max(1, Math.ceil(filteredPayments.length / PAGE_SIZE));
  const paginatedPayments = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredPayments.slice(start, start + PAGE_SIZE);
  }, [filteredPayments, page]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const getStatusBadge = (status) => {
    const value = String(status || '').toLowerCase();
    if (value === 'success') return 'bg-success';
    if (value === 'pending') return 'bg-warning text-dark';
    if (value === 'failed') return 'bg-danger';
    if (value === 'refunded') return 'bg-secondary';
    return 'bg-light text-dark';
  };

  return (
    <div className="container-fluid mt-3 rcpt-page">
      <div className="no-print">
        <div className="lib-page-header">
          <div className="lib-page-header__left">
            <div className="lib-page-header__icon"><i className="fas fa-file-invoice" /></div>
            <div>
              <h3 className="lib-page-header__title">Receipt Print</h3>
              <div className="lib-page-header__subtitle">Print payment receipts</div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm mt-3 rcpt-card">
          <div className="card-body">
            <div className="rcpt-toolbar">
              <div className="rcpt-search">
                <i className="fas fa-search" />
                <input
                  type="text"
                  placeholder="Search member, mobile, type, ref..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    type="button"
                    className="rcpt-search__clear"
                    onClick={() => setSearch('')}
                    aria-label="Clear search"
                  >
                    <i className="fas fa-times" />
                  </button>
                )}
              </div>

              <button
                type="button"
                className="rcpt-reset-btn"
                onClick={() => setSearch('')}
                disabled={!search}
              >
                <i className="fas fa-rotate-left me-1" />
                Reset
              </button>

              <span className="rcpt-toolbar__count">
                {filteredPayments.length} {filteredPayments.length === 1 ? 'receipt' : 'receipts'}
              </span>
            </div>

            {showLoader ? (
              <Loader />
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle rcpt-table">
                  <thead className="table-light">
                    <tr>
                      <th className="rcpt-col-no">#</th>
                      <th>Member</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Mode</th>
                      <th>Ref</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedPayments.length > 0 ? (
                      paginatedPayments.map((payment, index) => (
                        <tr key={payment.id}>
                          <td className="rcpt-col-no">{(page - 1) * PAGE_SIZE + index + 1}</td>

                          <td>
                            <span className="rcpt-name">{getPaymentName(payment)}</span>
                            <span className="rcpt-sub">{payment.mobile || '-'}</span>
                          </td>

                          <td>
                            <span className="rcpt-pill">
                              {String(payment.payment_type || '-').replace(/_/g, ' ')}
                            </span>
                          </td>

                          <td className="rcpt-amount">₹ {Number(payment.amount || 0)}</td>
                          <td className="text-capitalize">{payment.payment_mode || '-'}</td>
                          <td className="rcpt-sub">{payment.transaction_ref || '-'}</td>

                          <td>
                            <span className={`badge ${getStatusBadge(payment.payment_status)}`}>
                              {payment.payment_status || '-'}
                            </span>
                          </td>

                          <td className="rcpt-sub">
                            {payment.created_at ? String(payment.created_at).slice(0, 10) : '-'}
                          </td>

                          <td className="text-end">
                            <button
                              type="button"
                              className="rcpt-print-btn"
                              onClick={() => handlePrint(payment)}
                            >
                              <i className="fas fa-print" />
                              Print
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9">
                          <div className="rcpt-empty">
                            <span className="rcpt-empty__icon">
                              <i className="fas fa-file-invoice" />
                            </span>
                            <span className="rcpt-empty__title">No payments found</span>
                            <span className="rcpt-empty__hint">
                              {search ? 'Try clearing the search above.' : 'Receipts appear here once a payment is recorded.'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <Pagination
                  page={page}
                  totalPages={totalPages}
                  totalItems={filteredPayments.length}
                  itemLabel="receipts"
                  onPageChange={setPage}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedPayment && (
        <div className="receipt-print-area">
          <div className="receipt-box">
            <div className="receipt-title">
              <div className="lib-receipt-org">Community Library Management System</div>
              <h3 className="lib-receipt-heading">Library Payment Receipt</h3>
              <div className="lib-receipt-detail lib-ref-text">Receipt No: LIB-PAY-{selectedPayment.id}</div>
              <div className="lib-receipt-detail small text-muted">Generated: {new Date().toLocaleString('en-IN')}</div>
            </div>

            <table className="table table-bordered lib-receipt-detail">
              <tbody>
                <tr>
                  <th>Member Name</th>
                  <td>{getPaymentName(selectedPayment)}</td>
                </tr>

                <tr>
                  <th>Mobile</th>
                  <td>{selectedPayment.mobile || '-'}</td>
                </tr>

                <tr>
                  <th>Payment Type</th>
                  <td className="text-capitalize">{String(selectedPayment.payment_type || '-').replace(/_/g, ' ')}</td>
                </tr>

                <tr>
                  <th>Amount</th>
                  <td>₹ {Number(selectedPayment.amount || 0)}</td>
                </tr>

                <tr>
                  <th>Payment Mode</th>
                  <td className="text-capitalize">{selectedPayment.payment_mode || '-'}</td>
                </tr>

                <tr>
                  <th>Transaction Ref</th>
                  <td className="lib-ref-text">{selectedPayment.transaction_ref || '-'}</td>
                </tr>

                <tr>
                  <th>Payment Date</th>
                  <td>{selectedPayment.created_at ? String(selectedPayment.created_at).slice(0, 10) : '-'}</td>
                </tr>

                <tr>
                  <th>Remarks</th>
                  <td>{selectedPayment.remarks || '-'}</td>
                </tr>
              </tbody>
            </table>

            <div className="d-flex justify-content-between mt-5">
              <div>Member Signature</div>
              <div>Received By</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiptPrint;
